import React from 'react';
import PropTypes from 'prop-types';
import { Card, Badge } from 'react-bootstrap';

const ReportCard = ({ 
  title, 
  value, 
  icon, 
  color = "primary", 
  subtitle, 
  trend = null,
  size = "md",
  className = "",
  loading = false,
  onClick = null,
  animateOnMount = true
}) => {
  const getSizeClasses = () => {
    switch (size) {
      case 'sm':
        return { cardBody: 'p-3', title: 'h6', value: 'h5', icon: 'fs-3' };
      case 'lg':
        return { cardBody: 'p-4', title: 'h5', value: 'h2', icon: 'fs-1' };
      case 'xl':
        return { cardBody: 'p-4', title: 'h4', value: 'h1', icon: 'fs-1' };
      default:
        return { cardBody: 'p-3', title: 'h6', value: 'h3', icon: 'fs-2' };
    }
  };

  const sizeClasses = getSizeClasses();

  const getTrendIcon = () => {
    if (!trend && trend !== 0) return null;
    if (trend > 0) return '📈';
    if (trend < 0) return '📉';
    return '➡️';
  };

  const getTrendColor = () => {
    if (!trend && trend !== 0) return 'secondary';
    if (trend > 0) return 'success';
    if (trend < 0) return 'danger';
    return 'secondary';
  };

  const cardClasses = [
    'h-100',
    'shadow-sm',
    'border-0',
    className,
    onClick ? 'cursor-pointer' : '',
    animateOnMount ? 'fade-in' : ''
  ].filter(Boolean).join(' ');

  const handleCardClick = () => {
    if (onClick && !loading) {
      onClick();
    }
  };

  if (loading) {
    return (
      <Card className={cardClasses}>
        <Card.Body className={sizeClasses.cardBody}>
          <div className="d-flex justify-content-between align-items-start">
            <div className="flex-grow-1">
              <div className="placeholder-glow">
                <span className="placeholder col-6"></span>
              </div>
              <div className="placeholder-glow mt-2">
                <span className="placeholder col-8"></span>
              </div>
              {subtitle && (
                <div className="placeholder-glow mt-2">
                  <span className="placeholder col-4"></span>
                </div>
              )}
            </div>
            <div className="placeholder-glow">
              <span className="placeholder col-12" style={{ width: '40px', height: '40px' }}></span>
            </div>
          </div>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card 
      className={cardClasses}
      onClick={handleCardClick}
      style={{
        transition: onClick ? 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out' : 'none',
        cursor: onClick ? 'pointer' : 'default'
      }}
      onMouseEnter={(e) => {
        if (onClick) {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
        }
      }}
      onMouseLeave={(e) => {
        if (onClick) {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '';
        }
      }}
    >
      <Card.Body className={sizeClasses.cardBody}>
        <div className="d-flex justify-content-between align-items-start">
          <div className="flex-grow-1">
            <h6 className={`text-muted mb-2 ${sizeClasses.title} text-uppercase fw-normal`} style={{ letterSpacing: '0.5px' }}>
              {title}
            </h6>
            <div className={`mb-0 fw-bold ${sizeClasses.value} text-dark`} style={{ lineHeight: '1.2' }}>
              {value}
            </div>
            {subtitle && (
              <div className="d-flex align-items-center mt-2 flex-wrap">
                <small className="text-muted me-2">{subtitle}</small>
                {(trend !== null && trend !== undefined) && (
                  <Badge 
                    bg={getTrendColor()} 
                    className="d-flex align-items-center"
                    style={{ fontSize: '0.75rem' }}
                  >
                    <span className="me-1">{getTrendIcon()}</span>
                    {Math.abs(trend).toFixed(1)}%
                  </Badge>
                )}
              </div>
            )}
          </div>
          <div 
            className={`   ${sizeClasses.icon} ms-3 d-flex align-items-center justify-content-center`} 
            style={{ 
              minWidth: size === 'lg' || size === 'xl' ? '60px' : '50px',
              height: size === 'lg' || size === 'xl' ? '60px' : '50px',
              borderRadius: '12px',
              backgroundColor: `var(--bs-${color})`,
              color: '#fff',
              opacity: 0.9
            }}
          >
            {icon}
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};

// PropTypes for ReportCard
ReportCard.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  icon: PropTypes.element.isRequired,
  color: PropTypes.oneOf(['primary', 'secondary', 'success', 'danger', 'warning', 'info', 'light', 'dark']),
  subtitle: PropTypes.string,
  trend: PropTypes.number,
  size: PropTypes.oneOf(['sm', 'md', 'lg', 'xl']),
  className: PropTypes.string,
  loading: PropTypes.bool,
  onClick: PropTypes.func,
  animateOnMount: PropTypes.bool,
};

export default ReportCard;